import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "overflow-x-auto" }
const _hoisted_2 = {
  key: 0,
  class: "text-center py-8 text-gray-500"
}
const _hoisted_3 = {
  key: 1,
  class: "min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden"
}
const _hoisted_4 = { class: "bg-gray-50" }
const _hoisted_5 = { class: "divide-y divide-gray-200" }
const _hoisted_6 = { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900" }
const _hoisted_7 = { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900" }
const _hoisted_8 = { class: "px-6 py-4 text-sm" }
const _hoisted_9 = {
  key: 0,
  class: "flex flex-wrap gap-1"
}
const _hoisted_10 = {
  key: 0,
  class: "inline-block px-2 py-0.5 text-xs font-medium rounded-full opacity-75 bg-gray-400 text-gray-700"
}
const _hoisted_11 = {
  key: 1,
  class: "text-gray-500"
}
const _hoisted_12 = { class: "px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" }
const _hoisted_13 = { class: "px-6 py-4 text-sm text-gray-500 max-w-xs truncate" }

import { computed, ref } from "vue";
import { GroupTag, WeightRecord } from "@/types/v2";
import { useVaccinationTaskOperations } from "@/composables/tasks/vaccination/useVaccinationOperations";

// Simple date formatter function
interface Props {
  records: WeightRecord[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WeightRecordsTable',
  props: {
    records: {}
  },
  setup(__props: any) {

const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return 'Invalid date';
  }
};

const props = __props;

const { getTagStyles } = useVaccinationTaskOperations();

const getGroupTags = (record: WeightRecord): GroupTag[] => {
  if (!record.group?.tags) return [];
  return record.group.tags.filter((tag: GroupTag) => tag.title !== "Start Tag");
};

const getGroupSortValue = (record: WeightRecord): string => {
  if (!record.group) return "";
  const tags = getGroupTags(record);
  if (!tags.length) return "Untagged";
  return tags.map((t) => t.title).join(", ");
};

// State for sorting
const sortField = ref<keyof WeightRecord>('timestamp');
const sortDirection = ref<'asc' | 'desc'>('desc');

// Sorted records
const sortedRecords = computed(() => {
  if (!props.records || !props.records.length) return [];

  return [...props.records].sort((a, b) => {
    if (sortField.value === 'timestamp') {
      const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return sortDirection.value === 'asc' ? dateA - dateB : dateB - dateA;
    }

    if (sortField.value === 'tank') {
      const tankNameA = a.tank ? a.tank.name ?? a.tank.label: '';
      const tankNameB = b.tank ? b.tank.name ?? b.tank.label : '';
      return sortDirection.value === 'asc'
          ? tankNameA.localeCompare(tankNameB)
          : tankNameB.localeCompare(tankNameA);
    }

    if (sortField.value === 'group') {
      const groupNameA = getGroupSortValue(a);
      const groupNameB = getGroupSortValue(b);
      return sortDirection.value === 'asc'
          ? groupNameA.localeCompare(groupNameB)
          : groupNameB.localeCompare(groupNameA);
    }

    if (sortField.value === 'weight') {
      return sortDirection.value === 'asc'
          ? a.weight - b.weight
          : b.weight - a.weight;
    }

    if (sortField.value === 'comment') {
      const commentA = a.comment || '';
      const commentB = b.comment || '';
      return sortDirection.value === 'asc'
          ? commentA.localeCompare(commentB)
          : commentB.localeCompare(commentA);
    }

    // Default case
    return 0;
  });
});

// Toggle sort direction
const toggleSort = (field: keyof WeightRecord) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortDirection.value = 'asc';
  }
};

// Get sort indicator
const getSortIndicator = (field: keyof WeightRecord) => {
  if (sortField.value !== field) return '';
  return sortDirection.value === 'asc' ? '▲' : '▼';
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!_ctx.records || _ctx.records.length === 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, " No weight records available "))
      : (_openBlock(), _createElementBlock("table", _hoisted_3, [
          _createElementVNode("thead", _hoisted_4, [
            _createElementVNode("tr", null, [
              _createElementVNode("th", {
                onClick: _cache[0] || (_cache[0] = ($event: any) => (toggleSort('timestamp'))),
                class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              }, " Date " + _toDisplayString(getSortIndicator('timestamp')), 1),
              _createElementVNode("th", {
                onClick: _cache[1] || (_cache[1] = ($event: any) => (toggleSort('tank'))),
                class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              }, " Tank " + _toDisplayString(getSortIndicator('tank')), 1),
              _createElementVNode("th", {
                onClick: _cache[2] || (_cache[2] = ($event: any) => (toggleSort('group'))),
                class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              }, " Group " + _toDisplayString(getSortIndicator('group')), 1),
              _createElementVNode("th", {
                onClick: _cache[3] || (_cache[3] = ($event: any) => (toggleSort('weight'))),
                class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              }, " Weight (g) " + _toDisplayString(getSortIndicator('weight')), 1),
              _createElementVNode("th", {
                onClick: _cache[4] || (_cache[4] = ($event: any) => (toggleSort('comment'))),
                class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              }, " Comment " + _toDisplayString(getSortIndicator('comment')), 1)
            ])
          ]),
          _createElementVNode("tbody", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedRecords.value, (record, index) => {
              return (_openBlock(), _createElementBlock("tr", {
                key: index,
                class: "hover:bg-gray-50"
              }, [
                _createElementVNode("td", _hoisted_6, _toDisplayString(record.timestamp ? formatDate(record.timestamp) : 'N/A'), 1),
                _createElementVNode("td", _hoisted_7, _toDisplayString(record.tank ? (record.tank.label || record.tank.name) : 'N/A'), 1),
                _createElementVNode("td", _hoisted_8, [
                  (record.group)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getGroupTags(record), (tag) => {
                          return (_openBlock(), _createElementBlock("span", {
                            key: tag.key,
                            class: "inline-block px-2 py-0.5 text-xs font-medium rounded-full",
                            style: _normalizeStyle(_unref(getTagStyles)(tag))
                          }, _toDisplayString(tag.title), 5))
                        }), 128)),
                        (getGroupTags(record).length === 0)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_10, " Untagged "))
                          : _createCommentVNode("", true)
                      ]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_11, "N/A"))
                ]),
                _createElementVNode("td", _hoisted_12, _toDisplayString(record.weight), 1),
                _createElementVNode("td", _hoisted_13, _toDisplayString(record.comment || 'No comment'), 1)
              ]))
            }), 128))
          ])
        ]))
  ]))
}
}

})